<template>
  <div class="video-carousel">
    <div ref="tabsRef" class="video-infos-tabs">
      <div
        v-for="(item, index) in infos"
        :key="item.title"
        :style="{
          '--data-percent': activeIndex === index ? 1 - progress : 1,
        }"
        :class="{ active: activeIndex === index }"
        class="video-carousel-tab-item"
        @click="goTo(index)"
      >
        <p class="title">{{ item.title }}</p>
        <p v-if="item.tabDesc" class="desc">{{ item.tabDesc }}</p>
      </div>
    </div>
    <div class="video-container-wrapper rounded-[12px] overflow-hidden">
      <VideoPlayer
        ref="videoRef"
        :class="`w-full h-full video_cover`"
        :url="src"
        :poster="poster"
        :autoplay="autoplay"
        :loop="false"
        :muted="isMuted"
        :enable-fullscreen="false"
        loading="lazy"
        @ended="next"
        @time-update="timeUpdate"
      />
    </div>
    <div class="dots">
      <Dot
        v-for="(item, index) in infos"
        :key="item.title"
        :active="activeIndex === index"
        @click="goTo(index)"
      />
    </div>
    <div class="text-content" v-if="infos[activeIndex].desc">
      <h3 v-if="infos[activeIndex].title">{{ infos[activeIndex].subTitle }}</h3>
      <p>{{ infos[activeIndex].desc }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import VideoPlayer from '~/components/video-player/index.vue';
import Dot from '~/components/xDot/index.vue';

const props = defineProps<{
  infos: {
    title: string;
    url: string;
    poster: string;
    desc?: string;
    subTitle?: string;
    tabDesc?: string;
  }[];
  autoplay?: boolean;
}>();

const activeIndex = ref(0);
const tabsRef = ref<HTMLElement | null>(null);
const videoRef = ref();
const src = ref(props.infos[activeIndex.value].url);
const poster = ref(props.infos[activeIndex.value].poster);

const progress = ref(0);
const pendingAutoNext = ref(false);
let progressTimer: ReturnType<typeof setInterval> | null = null;

const injectedMuted = inject<Ref<boolean> | boolean>('isMuted', true);
const isMuted = computed(() => {
  if (isRef(injectedMuted)) {
    return Boolean(injectedMuted.value);
  }
  return Boolean(injectedMuted);
});

const goTo = (index: number) => {
  activeIndex.value = index;
};

const clearProgressTimer = () => {
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimer = null;
  }
};

const syncProgressFromPlayer = () => {
  const duration = videoRef.value?.duration?.();
  const currentTime = videoRef.value?.currentTime?.();

  if (
    typeof duration !== 'number' ||
    Number.isNaN(duration) ||
    !Number.isFinite(duration) ||
    duration <= 0 ||
    typeof currentTime !== 'number' ||
    Number.isNaN(currentTime) ||
    !Number.isFinite(currentTime)
  ) {
    return;
  }

  const nextProgress = Math.max(0, Math.min(1, currentTime / duration));
  progress.value = nextProgress;

  if (!pendingAutoNext.value && duration - currentTime <= 0.25) {
    pendingAutoNext.value = true;
    next();
  }
};

const startProgressTimer = () => {
  clearProgressTimer();
  progressTimer = setInterval(() => {
    syncProgressFromPlayer();
  }, 200);
};

watch(
  () => activeIndex.value,
  async val => {
    progress.value = 0;
    pendingAutoNext.value = false;
    src.value = props.infos[val].url;
    poster.value = props.infos[val].poster;
    await nextTick();
    scrollTabIntoView(val);
    startProgressTimer();
  },
);

function next() {
  if (activeIndex.value === props.infos.length - 1) {
    activeIndex.value = 0;
  } else {
    activeIndex.value++;
  }
}

function timeUpdate(currentTime: number) {
  const duration = videoRef.value?.duration?.();
  if (!duration || Number.isNaN(duration) || !Number.isFinite(duration)) {
    return;
  }
  progress.value = Math.max(0, Math.min(1, currentTime / duration));
}

const scrollTabIntoView = (tabIndex: number) => {
  if (!tabsRef.value) return;

  const tabElements = tabsRef.value.querySelectorAll<HTMLElement>('.video-carousel-tab-item');
  const targetTab = tabElements[tabIndex];

  if (!targetTab) return;

  const container = tabsRef.value;
  const tabLeft = targetTab.offsetLeft;
  const nextScrollLeft = tabLeft - (container.clientWidth - targetTab.offsetWidth) / 2;

  container.scrollTo({
    behavior: 'smooth',
    left: Math.max(0, nextScrollLeft),
  });
};

onMounted(() => {
  scrollTabIntoView(activeIndex.value);
  startProgressTimer();
});

onBeforeUnmount(() => {
  clearProgressTimer();
});
</script>

<style lang="scss" scoped>
.video-carousel {
  @apply w-full flex-col items-center;

  .video-infos-tabs {
    display: flex;
    gap: 16px;
    margin-bottom: 40px;
    width: 100%;
  }

  .video-container-wrapper {
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    margin-bottom: 16px;
    border-radius: 12px;
  }
  .video_cover {
    :deep(video) {
      object-fit: cover;
    }
  }
  .video-carousel-tab-item {
    @apply flex-col items-center justify-center gap-2;
    position: relative;
    font-size: 14px;
    line-height: 14px;
    background-color: var(--x-white-1);
    color: var(--x-text-3);
    border-radius: 8px;
    cursor: pointer;
    overflow: hidden;
    padding: 16px 12px;

    p {
      @apply my-0;
    }

    .title {
      color: var(--x-text-1);
      text-align: center;
      font-size: 24px;
      font-style: normal;
      line-height: normal;
    }
    .desc {
      color: var(--x-text-5);
      text-align: center;
      font-size: 14px;
      font-style: normal;
      line-height: normal;
    }

    &::before {
      opacity: 0.2;
      content: ' ';
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      left: 0;
      background-color: white;
      transition: transform 0.3s linear;
      transform: translateX(calc(var(--data-percent) * -100%));
    }
    &:hover {
      background-color: var(--x-back-5);
      .title {
        color: var(--x-text-1);
      }
      .desc {
        color: var(--x-text-5);
      }
    }
    &.active {
      background-color: var(--x-text-1);
      color: var(--x-white-1);
      .title {
        color: var(--x-white-1);
      }
      .desc {
        color: var(--x-white-5);
      }
    }
  }
  .dots {
    @apply flex-center gap-4;
  }
  .text-content {
    @apply flex-col gap-6 w-full mt-4;
    h3 {
      color: var(--x-text-1);
      font-size: 24px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      margin: 0;
    }
    p {
      margin: 0;
      color: var(--x-text-3);
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 32px; /* 200% */
      white-space: pre-line;
    }
  }
}

@media (max-width: $media-mobile) {
  .video-carousel {
    .video-infos-tabs {
      // width: 100%;
      width: 100vw;
      display: flex;
      gap: 8px;
      overflow-x: auto;
      overflow-y: hidden;
      flex-wrap: nowrap;
      margin-bottom: 24px;
      margin-top: 24px;
      padding-left: 20px;
      padding-right: 20px;
      padding-bottom: 4px;
      scroll-behavior: smooth;
      scrollbar-width: none;
      -ms-overflow-style: none;
      -webkit-overflow-scrolling: touch;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .video-carousel-tab-item {
      flex-shrink: 0;
      box-sizing: border-box;
    }
  }
}
</style>
